import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Plus, X, Search, Download } from 'lucide-react';
import { Switch } from './@/components/ui/switch';
import { displayRole as displayRoleFn } from './roleLabel';
import './TeamOverview.css';

const TeamOverview = ({ team, setTeam, recentlyChangedHours, selectedWeek, partnerId, config }) => {
  const displayRole = (role) => displayRoleFn(role, partnerId);
  const [expandedCards, setExpandedCards] = useState({});
  const [showPreferences, setShowPreferences] = useState({});
  const [showOverrides, setShowOverrides] = useState({});
  const [showSecondaryRoleInput, setShowSecondaryRoleInput] = useState({});
  const [unavailabilityForm, setUnavailabilityForm] = useState({});
  const [softPreferenceForm, setSoftPreferenceForm] = useState({});
  const [setShiftForm, setSetShiftForm] = useState({});
  const [idealShiftLengthForm, setIdealShiftLengthForm] = useState({});
  const [idealShiftsForm, setIdealShiftsForm] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  // Day toggles (availability/preferences), the unavailability / soft-preference /
  // set-shift day pickers and Contractual_Days_Off all store a numeric index. The
  // solver reads that index relative to the site's week start: config.DAY_NAMES is
  // the ordered index→day map (e.g. Moto starts Sunday, so index 0 = Sunday). Label
  // the cells from DAY_NAMES so the index a user clicks lines up with the backend's —
  // otherwise a Mon/Tue/Wed selection lands on Sun/Mon/Tue on a Sunday-start week.
  // Falls back to Monday-first (then WEEK_START_DAY rotation) when DAY_NAMES is absent.
  const daysOfWeek = useMemo(() => {
    const ABBR = { SUNDAY: 'Sun', MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat' };
    const names = config?.DAY_NAMES;
    if (Array.isArray(names) && names.length === 7) {
      return names.map(n => ABBR[String(n).toUpperCase()] || String(n).slice(0, 3));
    }
    const CANON = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    const start = CANON.indexOf(String(config?.WEEK_START_DAY || 'MONDAY').toUpperCase());
    const ordered = start > 0 ? [...CANON.slice(start), ...CANON.slice(0, start)] : CANON;
    return ordered.map(d => ABBR[d]);
  }, [config]);
  // Source the list of roles from the site's config (ROLES) so that roles configured
  // for the site are available as secondary-role options even if no team member
  // currently has that role as their primary. Fall back to team-derived roles
  // when a config isn't available.
  const roleOptions = useMemo(() => {
    const configRoles = config && config.ROLES ? Object.keys(config.ROLES) : [];
    const teamRoles = team.map(member => member.Role_Type).filter(Boolean);
    return [...new Set([...configRoles, ...teamRoles])];
  }, [config, team]);

  // Filter and group members
  const { activeGroups, inactiveMembers } = useMemo(() => {
    const active = {};
    const inactive = [];

    team.forEach((member, index) => {
      const matchesSearch = !searchQuery ||
        member.Name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.Role_Name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === 'all' || member.Role_Type === roleFilter;

      if (!matchesSearch || !matchesRole) return;

      if (member.Active === false) {
        inactive.push({ member, index });
      } else {
        const role = member.Role_Type || 'Other';
        if (!active[role]) active[role] = [];
        active[role].push({ member, index });
      }
    });

    return { activeGroups: active, inactiveMembers: inactive };
  }, [team, searchQuery, roleFilter]);

  // Handlers
  const handleHoursChange = (memberIndex, newHours) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      newTeam[memberIndex] = { ...newTeam[memberIndex], Changed_Expected_Hours: newHours };
      return newTeam;
    });
  };

  const handleDaysChange = (memberIndex, newDays) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      newTeam[memberIndex] = { ...newTeam[memberIndex], Expected_Days: newDays };
      return newTeam;
    });
  };

  const toggleField = (memberIndex, field, value) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      newTeam[memberIndex] = { ...newTeam[memberIndex], [field]: value };
      return newTeam;
    });
  };

  const handleSkillLevelChange = (memberIndex, newSkillLevel) => {
    if (Number.isNaN(newSkillLevel)) return;
    const clamped = Math.max(1, Math.min(5, newSkillLevel));
    toggleField(memberIndex, 'Skill_Level', clamped);
  };

  const toggleAvailability = (memberIndex, dayIndex, type) => {
    if (type !== 'Contractual_Days_Off' && team[memberIndex]["Contractual_Days_Off"]?.includes(dayIndex)) {
      return;
    }
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member[type] = member[type].includes(dayIndex)
        ? member[type].filter(day => day !== dayIndex)
        : [...member[type], dayIndex];
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addSecondaryRole = (memberIndex, role) => {
    if (!role) return;
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      if (!member.Secondary_Role_Type) member.Secondary_Role_Type = [];
      if (!member.Secondary_Role_Type.includes(role)) {
        member.Secondary_Role_Type = [...member.Secondary_Role_Type, role];
      }
      newTeam[memberIndex] = member;
      return newTeam;
    });
    setShowSecondaryRoleInput(prev => ({ ...prev, [memberIndex]: false }));
  };

  const removeSecondaryRole = (memberIndex, roleToRemove) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      if (member.Secondary_Role_Type) {
        member.Secondary_Role_Type = member.Secondary_Role_Type.filter(r => r !== roleToRemove);
      }
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addUnavailabilityBlock = (memberIndex, block) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Unavailability_Blocks = [...(member.Unavailability_Blocks || []), block];
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeUnavailabilityBlock = (memberIndex, blockIndex) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Unavailability_Blocks = member.Unavailability_Blocks.filter((_, i) => i !== blockIndex);
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addSoftPreferenceBlock = (memberIndex, block) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Soft_Preference_Blocks = [...(member.Soft_Preference_Blocks || []), block];
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeSoftPreferenceBlock = (memberIndex, blockIndex) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Soft_Preference_Blocks = member.Soft_Preference_Blocks.filter((_, i) => i !== blockIndex);
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const computeSetShiftFields = (day, start, end, role, constraintType) => {
    // Compute date from selectedWeek + day index
    let date = '';
    if (selectedWeek) {
      const weekStart = new Date(selectedWeek + 'T00:00:00');
      const shiftDate = new Date(weekStart);
      shiftDate.setDate(shiftDate.getDate() + day);
      date = shiftDate.toISOString().split('T')[0];
    }

    // Compute length in hours from start/end times
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    if (endMinutes <= startMinutes) endMinutes += 24 * 60; // cross-midnight
    const length = (endMinutes - startMinutes) / 60;

    // Convert to 24+ format for cross-midnight
    let endFormatted = end;
    if (endH < 5 && startH >= 5) {
      endFormatted = `${endH + 24}:${String(endM).padStart(2, '0')}`;
    }

    return {
      day,
      start,
      end: endFormatted,
      date,
      role,
      constraint_type: constraintType,
      length: Math.round(length * 100) / 100,
    };
  };

  const addSetShift = (memberIndex, shift) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Set_Shifts = [...(member.Set_Shifts || []), shift];
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeSetShift = (memberIndex, shiftIndex) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Set_Shifts = member.Set_Shifts.filter((_, i) => i !== shiftIndex);
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const getAvailableRoles = (member) => {
    const usedRoles = [member.Role_Type, "Manager", ...(member.Secondary_Role_Type || [])];
    return roleOptions.filter(role => !usedRoles.includes(role));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Schedule overrides — internal field names (match models.py TeamMember)
  const OVERRIDE_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  // Canonical operational areas (RoleConfiguration.operationalArea value space) — fixed
  // list so a person's Section_Opener/Closer areas can't drift from SECTIONS config keys.
  const SECTION_AREAS = ['FRONT_OF_HOUSE', 'BACK_OF_HOUSE', 'BAR'];
  const AREA_SHORT = { FRONT_OF_HOUSE: 'FOH', BACK_OF_HOUSE: 'BOH', BAR: 'Bar' };

  // Accept team.json variants: "monday", "MONDAY", "Monday" — display/merge consistently.
  const normalizeDay = (d) => (d == null ? '' : d.toString().toUpperCase());
  const dayOrderIndex = (d) => {
    const i = OVERRIDE_DAYS.indexOf(normalizeDay(d));
    return i === -1 ? 99 : i;
  };
  const sortByDay = (entries) => [...entries].sort(([a], [b]) => dayOrderIndex(a) - dayOrderIndex(b));
  const findBucketKey = (obj, day) => {
    const target = normalizeDay(day);
    return Object.keys(obj).find((k) => normalizeDay(k) === target) || target;
  };

  const updateOverrideField = (memberIndex, field, value) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      if (value === null || value === undefined || value === '') {
        delete member[field];
      } else {
        member[field] = value;
      }
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  // Section open/close designation: a person can open/close every role in the
  // listed operational areas. Priority map is keyed by area (lower = preferred).
  const SECTION_PRIORITY_FIELD = {
    Section_Opener_Operational_Areas: 'Section_Opener_Priority',
    Section_Closer_Operational_Areas: 'Section_Closer_Priority',
  };

  const toggleSectionArea = (memberIndex, field, area) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const current = member[field] || [];
      let next;
      if (current.includes(area)) {
        next = current.filter(a => a !== area);
        // Dropping the area also drops any priority we held for it.
        const pField = SECTION_PRIORITY_FIELD[field];
        if (member[pField] && member[pField][area] != null) {
          const pNext = { ...member[pField] };
          delete pNext[area];
          if (Object.keys(pNext).length === 0) delete member[pField];
          else member[pField] = pNext;
        }
      } else {
        next = [...current, area];
      }
      if (next.length === 0) delete member[field];
      else member[field] = next;
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const updateSectionPriority = (memberIndex, field, area, rawValue) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const next = { ...(member[field] || {}) };
      const parsed = parseIntOrNull(rawValue);
      if (parsed === null) delete next[area];
      else next[area] = parsed;
      if (Object.keys(next).length === 0) delete member[field];
      else member[field] = next;
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addIdealShiftLengthEntry = (memberIndex, day, hours) => {
    if (!day || hours === '' || hours === null || hours === undefined) return;
    const parsed = parseFloat(hours);
    if (Number.isNaN(parsed)) return;
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const next = { ...(member.Ideal_Shift_Length || {}) };
      const key = findBucketKey(next, day);
      next[key] = parsed;
      member.Ideal_Shift_Length = next;
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeIdealShiftLengthEntry = (memberIndex, day) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const next = { ...(member.Ideal_Shift_Length || {}) };
      delete next[day];
      if (Object.keys(next).length === 0) {
        delete member.Ideal_Shift_Length;
      } else {
        member.Ideal_Shift_Length = next;
      }
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addIdealShiftTuple = (memberIndex, day, start, end, weight) => {
    if (!day || !start || !end) return;
    const parsedWeight =
      weight === '' || weight === null || weight === undefined ? null : Number(weight);
    const hasWeight =
      parsedWeight !== null && !Number.isNaN(parsedWeight) && parsedWeight >= 0 && parsedWeight !== 1;
    const entry = hasWeight ? [start, end, parsedWeight] : [start, end];
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const existing = { ...(member.Ideal_Shifts || {}) };
      const key = findBucketKey(existing, day);
      existing[key] = [...(existing[key] || []), entry];
      member.Ideal_Shifts = existing;
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeIdealShiftTuple = (memberIndex, day, tupleIdx) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const existing = { ...(member.Ideal_Shifts || {}) };
      if (!existing[day]) return prevTeam;
      const nextDay = existing[day].filter((_, i) => i !== tupleIdx);
      if (nextDay.length === 0) {
        delete existing[day];
      } else {
        existing[day] = nextDay;
      }
      if (Object.keys(existing).length === 0) {
        delete member.Ideal_Shifts;
      } else {
        member.Ideal_Shifts = existing;
      }
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const addBreakTier = (memberIndex) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Break_Tiers = [...(member.Break_Tiers || []), { shift_length_range: [0, 8], break_length: 30 }];
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const removeBreakTier = (memberIndex, idx) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      const next = (member.Break_Tiers || []).filter((_, i) => i !== idx);
      if (next.length === 0) delete member.Break_Tiers;
      else member.Break_Tiers = next;
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const updateBreakTier = (memberIndex, idx, subfield, value) => {
    setTeam(prevTeam => {
      const newTeam = [...prevTeam];
      const member = { ...newTeam[memberIndex] };
      member.Break_Tiers = (member.Break_Tiers || []).map((tier, i) => {
        if (i !== idx) return tier;
        if (subfield === 'range_min') return { ...tier, shift_length_range: [parseFloat(value) || 0, tier.shift_length_range[1]] };
        if (subfield === 'range_max') return { ...tier, shift_length_range: [tier.shift_length_range[0], parseFloat(value) || 0] };
        if (subfield === 'break_length') return { ...tier, break_length: parseInt(value, 10) || 0 };
        return tier;
      });
      newTeam[memberIndex] = member;
      return newTeam;
    });
  };

  const validateOverrides = (member) => {
    const errors = [];
    const min = member.Min_Shift_Length;
    const max = member.Max_Shift_Length;
    if (min != null && min <= 0) errors.push('Min Shift Length must be > 0');
    if (max != null && max <= 0) errors.push('Max Shift Length must be > 0');
    if (min != null && max != null && min > max) errors.push('Min Shift Length cannot exceed Max Shift Length');
    const idealLen = member.Ideal_Shift_Length || {};
    Object.entries(idealLen).forEach(([day, v]) => {
      if (v <= 0) errors.push(`Ideal Shift Length [${day}] must be > 0`);
      if (min != null && v < min) errors.push(`Ideal Shift Length [${day}]=${v}h below Min=${min}h`);
      if (max != null && v > max) errors.push(`Ideal Shift Length [${day}]=${v}h above Max=${max}h`);
    });
    if (member.Min_Rest_Between_Shifts != null && member.Min_Rest_Between_Shifts < 0) {
      errors.push('Min Rest Between Shifts cannot be negative');
    }
    const wMin = member.Min_Workdays;
    const wMax = member.Max_Workdays;
    if (wMin != null && (wMin < 0 || wMin > 7)) errors.push('Min Workdays must be in [0, 7]');
    if (wMax != null && (wMax < 0 || wMax > 7)) errors.push('Max Workdays must be in [0, 7]');
    if (wMin != null && wMax != null && wMin > wMax) errors.push('Min Workdays cannot exceed Max Workdays');
    if (member.Max_Weekly_Hours != null && member.Max_Weekly_Hours <= 0) errors.push('Max Weekly Hours must be > 0');
    if (member.Min_Weekly_Hours != null && member.Min_Weekly_Hours <= 0) errors.push('Min Weekly Hours must be > 0');
    if (
      member.Min_Weekly_Hours != null &&
      member.Max_Weekly_Hours != null &&
      member.Min_Weekly_Hours > member.Max_Weekly_Hours
    ) {
      errors.push('Min Weekly Hours cannot exceed Max Weekly Hours');
    }
    if (
      member.Hours_Priority != null &&
      (!Number.isInteger(member.Hours_Priority) || member.Hours_Priority < 1 || member.Hours_Priority > 5)
    ) {
      errors.push('Hours Priority must be an integer in [1, 5]');
    }
    if (member.Max_Shifts_Per_Day != null && member.Max_Shifts_Per_Day < 1) errors.push('Max Shifts Per Day must be >= 1');
    if (member.Opening_Key_Holder_Priority != null && member.Opening_Key_Holder_Priority < 0) {
      errors.push('Opening Key Holder Priority cannot be negative');
    }
    if (member.Closing_Key_Holder_Priority != null && member.Closing_Key_Holder_Priority < 0) {
      errors.push('Closing Key Holder Priority cannot be negative');
    }
    [['Section_Opener_Priority', 'Opener'], ['Section_Closer_Priority', 'Closer']].forEach(([field, label]) => {
      Object.entries(member[field] || {}).forEach(([area, v]) => {
        if (v != null && v < 0) errors.push(`Section ${label} Priority [${AREA_SHORT[area] || area}] cannot be negative`);
      });
    });
    const idealShifts = member.Ideal_Shifts || {};
    Object.entries(idealShifts).forEach(([day, tuples]) => {
      (tuples || []).forEach((tuple, i) => {
        if (tuple.length === 3) {
          const w = tuple[2];
          if (typeof w !== 'number' || Number.isNaN(w) || w < 0) {
            errors.push(`Ideal Shifts [${day}][${i}] weight must be a non-negative number`);
          }
        }
      });
    });
    (member.Break_Tiers || []).forEach((tier, i) => {
      const [rMin, rMax] = tier.shift_length_range || [];
      if (rMin != null && rMin < 0) errors.push(`Break Tier ${i + 1}: shift range min cannot be negative`);
      if (rMax != null && rMax < 0) errors.push(`Break Tier ${i + 1}: shift range max cannot be negative`);
      if (rMin != null && rMax != null && rMin >= rMax) errors.push(`Break Tier ${i + 1}: shift range min must be less than max`);
      if (tier.break_length != null && tier.break_length < 0) errors.push(`Break Tier ${i + 1}: break length cannot be negative`);
    });
    return errors.length > 0 ? errors.join('; ') : null;
  };

  const parseNumOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const parseIntOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  };


  // Compact member card
  const renderMemberCard = ({ member, index: memberIndex }) => {
    const isExpanded = expandedCards[memberIndex];

    return (
      <div key={memberIndex} className={`team-card ${member.Active === false ? 'team-card--inactive' : ''}`}>
        {/* Compact header - always visible */}
        <div className="team-card__header" onClick={() => setExpandedCards(prev => ({ ...prev, [memberIndex]: !prev[memberIndex] }))}>
          <div className="team-card__avatar">{getInitials(member.Name)}</div>
          <div className="team-card__summary">
            <div className="team-card__top-row">
              <span className="team-card__name">{member.Name}</span>
              <div className="team-card__controls">
                <Switch
                  checked={member.Active !== false}
                  onCheckedChange={(checked) => toggleField(memberIndex, 'Active', checked)}
                />
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>
            <span className="team-card__role">
              {member.Role_Name || displayRole(member.Role_Type)}
              {' · '}{(member.Assigned_Hours || 0).toFixed(0)}/{member.Changed_Expected_Hours || member.Weekly_Expected_Hours || 0}h
            </span>
          </div>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="team-card__body">
            {/* Hours & Days */}
            <div className="team-card__fields">
              <div className="team-card__field">
                <label>Expected Hours</label>
                <input type="number" value={member.Changed_Expected_Hours} onChange={(e) => handleHoursChange(memberIndex, parseFloat(e.target.value))} min="0" max="168" className="team-card__input" />
              </div>
              <div className="team-card__field">
                <label>Original Hours</label>
                <input type="number" value={member.Weekly_Expected_Hours} readOnly className="team-card__input team-card__input--readonly" />
              </div>
              <div className="team-card__field">
                <label>Expected Days</label>
                <input type="number" value={member.Expected_Days} onChange={(e) => handleDaysChange(memberIndex, parseFloat(e.target.value))} min="0" max="7" className="team-card__input" />
              </div>
              <div className="team-card__field">
                <label>Skill Level</label>
                <input type="number" value={member.Skill_Level} onChange={(e) => handleSkillLevelChange(memberIndex, parseInt(e.target.value))} min="1" max="5" className="team-card__input" />
              </div>
              <div className="team-card__field">
                <label>Contracted Constraint</label>
                <select
                  value={member.Contracted_Hours_Constraint || ''}
                  onChange={(e) => toggleField(memberIndex, 'Contracted_Hours_Constraint', e.target.value || null)}
                  className="team-card__input"
                >
                  <option value="">None</option>
                  <option value="MINIMUM">MINIMUM</option>
                  <option value="EXACT">EXACT</option>
                </select>
              </div>
              <div className="team-card__field">
                <label>Contracted Hours</label>
                <input
                  type="number"
                  value={member.Contracted_Hours ?? ''}
                  onChange={(e) => toggleField(memberIndex, 'Contracted_Hours', e.target.value === '' ? null : parseFloat(e.target.value))}
                  min="0"
                  max="168"
                  step="0.5"
                  className="team-card__input"
                />
              </div>
              <div className="team-card__field">
                <label>Contracted Days</label>
                <input
                  type="number"
                  value={member.Contracted_Days ?? ''}
                  onChange={(e) => toggleField(memberIndex, 'Contracted_Days', e.target.value === '' ? null : parseInt(e.target.value))}
                  min="0"
                  max="7"
                  step="1"
                  className="team-card__input"
                />
              </div>
            </div>

            {/* Toggles row */}
            <div className="team-card__toggles">
              <div className="team-card__toggle">
                <span>Enforce Hours</span>
                <Switch checked={member.Enforce_Expected_Hours} onCheckedChange={(v) => toggleField(memberIndex, 'Enforce_Expected_Hours', v)} />
              </div>
              <div className="team-card__toggle">
                <span>Enforce Days</span>
                <Switch checked={member.Enforce_Expected_Days} onCheckedChange={(v) => toggleField(memberIndex, 'Enforce_Expected_Days', v)} />
              </div>
              <div className="team-card__toggle">
                <span>International</span>
                <Switch checked={member.International_Worker} onCheckedChange={(v) => toggleField(memberIndex, 'International_Worker', v)} />
              </div>
              <div className="team-card__toggle">
                <span>Youth</span>
                <Switch checked={member.Youth_Worker} onCheckedChange={(v) => toggleField(memberIndex, 'Youth_Worker', v)} />
              </div>
              <div className="team-card__toggle">
                <span>Trainee</span>
                <Switch checked={member.Trainee} onCheckedChange={(v) => toggleField(memberIndex, 'Trainee', v)} />
              </div>
            </div>

            {/* Secondary Roles */}
            <div className="team-card__section">
              <span className="team-card__section-label">Secondary Roles</span>
              <div className="secondary-roles-tags">
                {member.Secondary_Role_Type && member.Secondary_Role_Type.map((role, i) => (
                  <div key={i} className="role-tag">
                    <span>{displayRole(role)}</span>
                    <button className="remove-tag-btn" onClick={() => removeSecondaryRole(memberIndex, role)}><X size={14} /></button>
                  </div>
                ))}
                {showSecondaryRoleInput[memberIndex] ? (
                  <select
                    className="form-select add-role-select"
                    onChange={(e) => addSecondaryRole(memberIndex, e.target.value)}
                    onBlur={() => setShowSecondaryRoleInput(prev => ({ ...prev, [memberIndex]: false }))}
                    value=""
                    autoFocus
                  >
                    <option value="" disabled>Select a role</option>
                    {getAvailableRoles(member).map((role, idx) => (
                      <option key={idx} value={role}>{displayRole(role)}</option>
                    ))}
                  </select>
                ) : (
                  <button className="add-role-tag" onClick={() => setShowSecondaryRoleInput(prev => ({ ...prev, [memberIndex]: true }))} disabled={getAvailableRoles(member).length === 0}>
                    <Plus size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Preferences */}
            <div className="preferences-toggle" onClick={() => setShowPreferences(prev => ({ ...prev, [memberIndex]: !prev[memberIndex] }))}>
              {showPreferences[memberIndex] ? <>Hide Preferences <ChevronUp size={16} /></> : <>Show Preferences <ChevronDown size={16} /></>}
            </div>
            {showPreferences[memberIndex] && (
              <div className="preferences-section">
                {['Early_Availability', 'Late_Availability'].map(type => (
                  <div key={type} className="team-card__pref-row">
                    <span className="team-card__section-label">{type.replace('_', ' ')}</span>
                    <div className="availability-container">
                      {daysOfWeek.map((day, i) => (
                        <span key={i} className={`availability-day ${member[type]?.includes(i) ? 'selected' : ''} ${member.Contractual_Days_Off?.includes(i) ? 'disabled' : ''}`}
                          onClick={() => toggleAvailability(memberIndex, i, type)}>{day}</span>
                      ))}
                    </div>
                  </div>
                ))}
                {['Early_Preference', 'Late_Preference'].map(type => (
                  <div key={type} className="team-card__pref-row">
                    <span className="team-card__section-label">{type.replace('_', ' ')}</span>
                    <div className="availability-container">
                      {daysOfWeek.map((day, i) => (
                        <span key={i} className={`availability-day ${member[type]?.includes(i) ? 'selected' : ''} ${member.Contractual_Days_Off?.includes(i) ? 'disabled' : ''}`}
                          onClick={() => toggleAvailability(memberIndex, i, type)}>{day}</span>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="unavailability-blocks">
                  <span className="team-card__section-label">Unavailability Blocks</span>
                  {(member.Unavailability_Blocks || []).map((block, blockIdx) => (
                    <div key={blockIdx} className="unavailability-block-row">
                      <span>{daysOfWeek[block.day]}</span>
                      <span>{block.start}</span><span>-</span><span>{block.end}</span>
                      <button className="remove-tag-btn" onClick={() => removeUnavailabilityBlock(memberIndex, blockIdx)}><X size={14} /></button>
                    </div>
                  ))}
                  <div className="unavailability-add-form">
                    <select value={unavailabilityForm[memberIndex]?.day ?? 0} onChange={(e) => setUnavailabilityForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], day: parseInt(e.target.value) } }))}>
                      {daysOfWeek.map((day, i) => <option key={i} value={i}>{day}</option>)}
                    </select>
                    <input type="time" value={unavailabilityForm[memberIndex]?.start || '09:00'} onChange={(e) => setUnavailabilityForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], start: e.target.value } }))} />
                    <input type="time" value={unavailabilityForm[memberIndex]?.end || '17:00'} onChange={(e) => setUnavailabilityForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], end: e.target.value } }))} />
                    <button className="add-role-btn" onClick={() => {
                      const form = unavailabilityForm[memberIndex] || {};
                      addUnavailabilityBlock(memberIndex, { day: form.day ?? 0, start: form.start || '09:00', end: form.end || '17:00' });
                    }}>Add</button>
                  </div>
                </div>
                <div className="soft-preference-blocks">
                  <span className="team-card__section-label">Soft Preference Blocks</span>
                  {(member.Soft_Preference_Blocks || []).map((block, blockIdx) => (
                    <div key={blockIdx} className="soft-preference-block-row">
                      <span>{daysOfWeek[block.day]}</span>
                      <span>{block.start}</span><span>-</span><span>{block.end}</span>
                      <button className="remove-tag-btn" onClick={() => removeSoftPreferenceBlock(memberIndex, blockIdx)}><X size={14} /></button>
                    </div>
                  ))}
                  <div className="soft-preference-add-form">
                    <select value={softPreferenceForm[memberIndex]?.day ?? 0} onChange={(e) => setSoftPreferenceForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], day: parseInt(e.target.value) } }))}>
                      {daysOfWeek.map((day, i) => <option key={i} value={i}>{day}</option>)}
                    </select>
                    <input type="time" value={softPreferenceForm[memberIndex]?.start || '09:00'} onChange={(e) => setSoftPreferenceForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], start: e.target.value } }))} />
                    <input type="time" value={softPreferenceForm[memberIndex]?.end || '17:00'} onChange={(e) => setSoftPreferenceForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], end: e.target.value } }))} />
                    <button className="add-role-btn" onClick={() => {
                      const form = softPreferenceForm[memberIndex] || {};
                      addSoftPreferenceBlock(memberIndex, { day: form.day ?? 0, start: form.start || '09:00', end: form.end || '17:00' });
                    }}>Add</button>
                  </div>
                </div>
                <div className="set-shifts-blocks">
                  <span className="team-card__section-label">Set Shifts</span>
                  {(member.Set_Shifts || []).map((ss, ssIdx) => (
                    <div key={ssIdx} className="set-shift-row">
                      <span>{daysOfWeek[ss.day]}</span>
                      <span>{ss.start}</span><span>-</span><span>{ss.end}</span>
                      <span className="set-shift-role">{displayRole(ss.role)}</span>
                      <span className={`set-shift-type set-shift-type--${ss.constraint_type.toLowerCase()}`}>{ss.constraint_type}</span>
                      <button className="remove-tag-btn" onClick={() => removeSetShift(memberIndex, ssIdx)}><X size={14} /></button>
                    </div>
                  ))}
                  <div className="set-shift-add-form">
                    <select value={setShiftForm[memberIndex]?.day ?? 0} onChange={(e) => setSetShiftForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], day: parseInt(e.target.value) } }))}>
                      {daysOfWeek.map((day, i) => <option key={i} value={i}>{day}</option>)}
                    </select>
                    <input type="time" value={setShiftForm[memberIndex]?.start || '09:00'} onChange={(e) => setSetShiftForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], start: e.target.value } }))} />
                    <input type="time" value={setShiftForm[memberIndex]?.end || '17:00'} onChange={(e) => setSetShiftForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], end: e.target.value } }))} />
                    <select value={setShiftForm[memberIndex]?.role || member.Role_Type} onChange={(e) => setSetShiftForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], role: e.target.value } }))}>
                      {roleOptions.map((role, idx) => <option key={idx} value={role}>{displayRole(role)}</option>)}
                    </select>
                    <select value={setShiftForm[memberIndex]?.constraint_type || 'HARD'} onChange={(e) => setSetShiftForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], constraint_type: e.target.value } }))}>
                      <option value="HARD">Hard</option>
                      <option value="SOFT">Soft</option>
                    </select>
                    <button className="add-role-btn" onClick={() => {
                      const form = setShiftForm[memberIndex] || {};
                      const shift = computeSetShiftFields(
                        form.day ?? 0,
                        form.start || '09:00',
                        form.end || '17:00',
                        form.role || member.Role_Type,
                        form.constraint_type || 'HARD'
                      );
                      addSetShift(memberIndex, shift);
                    }}>Add</button>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Overrides */}
            {(() => {
              const overrideFieldCount =
                ['Min_Shift_Length', 'Max_Shift_Length', 'Min_Rest_Between_Shifts', 'Max_Weekly_Hours',
                 'Min_Weekly_Hours', 'Hours_Priority',
                 'Min_Workdays', 'Max_Workdays', 'Max_Shifts_Per_Day', 'Work_Monday_Morning', 'Avoid_Busy_Periods',
                 'Is_Opening_Key_Holder', 'Is_Closing_Key_Holder',
                 'Opening_Key_Holder_Priority', 'Closing_Key_Holder_Priority']
                  .filter((f) => member[f] != null).length;
              const idealLenCount = Object.keys(member.Ideal_Shift_Length || {}).length;
              const idealShiftsCount = Object.values(member.Ideal_Shifts || {})
                .reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
              const sectionAreaCount =
                ((member.Section_Opener_Operational_Areas || []).length > 0 ? 1 : 0) +
                ((member.Section_Closer_Operational_Areas || []).length > 0 ? 1 : 0);
              const sectionPriorityCount =
                Object.keys(member.Section_Opener_Priority || {}).length +
                Object.keys(member.Section_Closer_Priority || {}).length;
              const breakTiersCount = (member.Break_Tiers || []).length;
              const total =
                overrideFieldCount + idealLenCount + idealShiftsCount + sectionAreaCount + sectionPriorityCount + breakTiersCount;
              return (
                <div className="preferences-toggle" onClick={() => setShowOverrides(prev => ({ ...prev, [memberIndex]: !prev[memberIndex] }))}>
                  <span>
                    {showOverrides[memberIndex] ? 'Hide Schedule Overrides' : 'Show Schedule Overrides'}
                    {total > 0 && <span className="overrides-count-badge">{total}</span>}
                  </span>
                  {showOverrides[memberIndex] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              );
            })()}
            {showOverrides[memberIndex] && (() => {
              const overrideError = validateOverrides(member);
              const idealLen = member.Ideal_Shift_Length || {};
              const idealShifts = member.Ideal_Shifts || {};
              const lenForm = idealShiftLengthForm[memberIndex] || {};
              const shiftsForm = idealShiftsForm[memberIndex] || {};
              const dayShort = (d) => normalizeDay(d).slice(0, 3);

              const numField = (label, field, props = {}) => (
                <label className="override-field">
                  <span className="override-field__label">{label}</span>
                  <div className="override-field__control">
                    <input
                      type="number"
                      className="override-field__input"
                      value={member[field] ?? ''}
                      onChange={(e) =>
                        updateOverrideField(
                          memberIndex,
                          field,
                          props.integer ? parseIntOrNull(e.target.value) : parseNumOrNull(e.target.value)
                        )
                      }
                      step={props.step ?? '0.5'}
                      min={props.min}
                      max={props.max}
                      placeholder="—"
                    />
                    {props.suffix && <span className="override-field__suffix">{props.suffix}</span>}
                  </div>
                </label>
              );

              const tristateToggle = (label, field) => {
                const state = member[field];
                return (
                  <div className={`override-toggle override-toggle--${state === true ? 'on' : state === false ? 'off' : 'unset'}`}>
                    <div className="override-toggle__text">
                      <span className="override-toggle__label">{label}</span>
                      <span className="override-toggle__state">
                        {state === true ? 'enabled' : state === false ? 'disabled' : 'inherits role default'}
                      </span>
                    </div>
                    <div className="override-toggle__controls">
                      <Switch
                        checked={state === true}
                        onCheckedChange={(v) => updateOverrideField(memberIndex, field, v)}
                      />
                      {state != null && (
                        <button
                          className="override-toggle__clear"
                          onClick={() => updateOverrideField(memberIndex, field, null)}
                          title="Clear override — use role default"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              };

              return (
                <div className="override-panel">
                  {overrideError && (
                    <div className="override-error">{overrideError}</div>
                  )}

                  <div className="override-group">
                    <div className="override-group__title">Shift Bounds</div>
                    <div className="override-grid">
                      {numField('Min Shift', 'Min_Shift_Length', { suffix: 'h', min: '0' })}
                      {numField('Max Shift', 'Max_Shift_Length', { suffix: 'h', min: '0' })}
                      {numField('Min Rest', 'Min_Rest_Between_Shifts', { suffix: 'h', min: '0' })}
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">
                      Weekly Hours <span className="override-group__hint">Min/Max bound rules; Priority weights deficit redistribution (higher = bigger share of cuts)</span>
                    </div>
                    <div className="override-grid">
                      {numField('Min Weekly', 'Min_Weekly_Hours', { suffix: 'h', min: '0' })}
                      {numField('Max Weekly', 'Max_Weekly_Hours', { suffix: 'h', min: '0' })}
                      {numField('Hours Priority', 'Hours_Priority', { integer: true, step: '1', min: '1', max: '5' })}
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">Workdays &amp; Frequency</div>
                    <div className="override-grid">
                      {numField('Min Workdays', 'Min_Workdays', { integer: true, step: '1', min: '0', max: '7' })}
                      {numField('Max Workdays', 'Max_Workdays', { integer: true, step: '1', min: '0', max: '7' })}
                      {numField('Shifts/Day', 'Max_Shifts_Per_Day', { integer: true, step: '1', min: '1' })}
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">Preferences</div>
                    <div className="override-toggles">
                      {tristateToggle('Work Monday Morning', 'Work_Monday_Morning')}
                      {tristateToggle('Avoid Busy Periods', 'Avoid_Busy_Periods')}
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">
                      Key Holder <span className="override-group__hint">overrides KEY_HOLDERS role list when set</span>
                    </div>
                    <div className="override-toggles">
                      {tristateToggle('Opening Key Holder', 'Is_Opening_Key_Holder')}
                      {tristateToggle('Closing Key Holder', 'Is_Closing_Key_Holder')}
                    </div>
                    <div className="override-grid">
                      {numField('Open Priority', 'Opening_Key_Holder_Priority', { integer: true, step: '1', min: '0' })}
                      {numField('Close Priority', 'Closing_Key_Holder_Priority', { integer: true, step: '1', min: '0' })}
                    </div>
                  </div>

                  {[
                    { listField: 'Section_Opener_Operational_Areas', priField: 'Section_Opener_Priority', verb: 'open' },
                    { listField: 'Section_Closer_Operational_Areas', priField: 'Section_Closer_Priority', verb: 'close' },
                  ].map(({ listField, priField, verb }, gi) => {
                    const areas = member[listField] || [];
                    const priorities = member[priField] || {};
                    return (
                      <div className="override-group" key={listField}>
                        <div className="override-group__title">
                          Section {verb === 'open' ? 'Opener' : 'Closer'}
                          {gi === 0 && (
                            <span className="override-group__hint">
                              areas this person can open/close (covers every role in the area); priority lower = preferred
                            </span>
                          )}
                        </div>
                        <div className="section-area-chips">
                          {SECTION_AREAS.map((area) => {
                            const selected = areas.includes(area);
                            return (
                              <button
                                key={area}
                                type="button"
                                className={`section-area-chip ${selected ? 'is-on' : ''}`}
                                onClick={() => toggleSectionArea(memberIndex, listField, area)}
                                title={`Can ${verb} ${area.replace(/_/g, ' ').toLowerCase()}`}
                              >
                                {AREA_SHORT[area]}
                              </button>
                            );
                          })}
                        </div>
                        {areas.length > 0 && (
                          <div className="override-grid">
                            {areas.map((area) => (
                              <label className="override-field" key={area}>
                                <span className="override-field__label">{AREA_SHORT[area]} priority</span>
                                <div className="override-field__control">
                                  <input
                                    type="number"
                                    className="override-field__input"
                                    value={priorities[area] ?? ''}
                                    onChange={(e) => updateSectionPriority(memberIndex, priField, area, e.target.value)}
                                    step="1"
                                    min="0"
                                    placeholder="—"
                                  />
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="override-group">
                    <div className="override-group__title">
                      Ideal Shift Length <span className="override-group__hint">per day, in hours</span>
                    </div>
                    {Object.keys(idealLen).length > 0 && (
                      <div className="override-chips">
                        {sortByDay(Object.entries(idealLen)).map(([day, hours]) => (
                          <div key={day} className="override-chip">
                            <span className="override-chip__day">{dayShort(day)}</span>
                            <span className="override-chip__value">{hours}h</span>
                            <button className="override-chip__remove" onClick={() => removeIdealShiftLengthEntry(memberIndex, day)}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="override-add-row">
                      <select
                        className="override-add-row__select"
                        value={lenForm.day || 'MONDAY'}
                        onChange={(e) => setIdealShiftLengthForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], day: e.target.value } }))}
                      >
                        {OVERRIDE_DAYS.map(d => <option key={d} value={d}>{dayShort(d)}</option>)}
                      </select>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        placeholder="hours"
                        className="override-add-row__input"
                        value={lenForm.hours || ''}
                        onChange={(e) => setIdealShiftLengthForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], hours: e.target.value } }))}
                      />
                      <button
                        className="override-add-row__btn"
                        onClick={() => {
                          addIdealShiftLengthEntry(memberIndex, lenForm.day || 'MONDAY', lenForm.hours);
                          setIdealShiftLengthForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], hours: '' } }));
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">
                      Ideal Shifts <span className="override-group__hint">per day · supports <code>open</code> / <code>close</code> / <code>HH:MM</code> · optional weight</span>
                    </div>
                    {Object.keys(idealShifts).length > 0 && (
                      <div className="override-chips">
                        {sortByDay(Object.entries(idealShifts)).flatMap(([day, tuples]) =>
                          (tuples || []).map((tuple, i) => {
                            const hasWeight = tuple.length === 3 && tuple[2] != null && tuple[2] !== 1;
                            return (
                              <div key={`${day}-${i}`} className="override-chip">
                                <span className="override-chip__day">{dayShort(day)}</span>
                                <span className="override-chip__value">
                                  {tuple[0]}<span className="override-chip__dash">→</span>{tuple[1]}
                                </span>
                                {hasWeight && (
                                  <span className="override-chip__weight">{tuple[2]}×</span>
                                )}
                                <button className="override-chip__remove" onClick={() => removeIdealShiftTuple(memberIndex, day, i)}>
                                  <X size={12} />
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                    <div className="override-add-row">
                      <select
                        className="override-add-row__select"
                        value={shiftsForm.day || 'MONDAY'}
                        onChange={(e) => setIdealShiftsForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], day: e.target.value } }))}
                      >
                        {OVERRIDE_DAYS.map(d => <option key={d} value={d}>{dayShort(d)}</option>)}
                      </select>
                      <input
                        type="text"
                        placeholder="start"
                        className="override-add-row__input override-add-row__input--time"
                        value={shiftsForm.start || ''}
                        onChange={(e) => setIdealShiftsForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], start: e.target.value } }))}
                      />
                      <span className="override-add-row__sep">→</span>
                      <input
                        type="text"
                        placeholder="end"
                        className="override-add-row__input override-add-row__input--time"
                        value={shiftsForm.end || ''}
                        onChange={(e) => setIdealShiftsForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], end: e.target.value } }))}
                      />
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        placeholder="×1"
                        className="override-add-row__input override-add-row__input--weight"
                        value={shiftsForm.weight || ''}
                        onChange={(e) => setIdealShiftsForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], weight: e.target.value } }))}
                        title="Optional weight — multiplies the LP bonus. Defaults to 1."
                      />
                      <button
                        className="override-add-row__btn"
                        onClick={() => {
                          addIdealShiftTuple(memberIndex, shiftsForm.day || 'MONDAY', shiftsForm.start, shiftsForm.end, shiftsForm.weight);
                          setIdealShiftsForm(prev => ({ ...prev, [memberIndex]: { ...prev[memberIndex], start: '', end: '', weight: '' } }));
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="override-group">
                    <div className="override-group__title">
                      Break Tiers <span className="override-group__hint">shift length range → break duration; overrides role break rules for this person</span>
                    </div>
                    <div className="override-break-tiers">
                      {(member.Break_Tiers || []).map((tier, idx) => (
                        <div key={idx} className="override-break-row">
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            className="override-break-num"
                            value={tier.shift_length_range[0]}
                            onChange={(e) => updateBreakTier(memberIndex, idx, 'range_min', e.target.value)}
                            placeholder="0"
                          />
                          <span className="override-break-sep">–</span>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            className="override-break-num"
                            value={tier.shift_length_range[1]}
                            onChange={(e) => updateBreakTier(memberIndex, idx, 'range_max', e.target.value)}
                            placeholder="0"
                          />
                          <span className="override-break-unit">h</span>
                          <span className="override-break-arrow">→</span>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            className="override-break-num"
                            value={tier.break_length}
                            onChange={(e) => updateBreakTier(memberIndex, idx, 'break_length', e.target.value)}
                            placeholder="0"
                          />
                          <span className="override-break-unit">min</span>
                          <button
                            className="override-chip__remove"
                            onClick={() => removeBreakTier(memberIndex, idx)}
                            title="Remove tier"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <button className="override-add-row__btn" onClick={() => addBreakTier(memberIndex)} title="Add break tier">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const totalActiveHours = useMemo(() => {
    return team.filter(m => m.Active !== false).reduce((sum, m) => sum + (m.Assigned_Hours || 0), 0);
  }, [team]);

  const downloadTeam = () => {
    const blob = new Blob([JSON.stringify(team, null, 4)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().split('T')[0];
    a.download = `team_${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="team-overview">
      {/* Search and filter bar */}
      <div className="team-toolbar">
        <div className="team-search">
          <Search size={16} className="team-search__icon" />
          <input
            type="text"
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="team-search__input"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="team-filter"
        >
          <option value="all">All Roles</option>
          {roleOptions.map(role => <option key={role} value={role}>{displayRole(role)}</option>)}
        </select>
        {totalActiveHours > 0 && (
          <span className="team-total-hours">{totalActiveHours.toFixed(1)}h total</span>
        )}
        <button
          type="button"
          className="team-download-btn"
          onClick={downloadTeam}
          disabled={!team || team.length === 0}
          title="Download current team as team.json"
        >
          <Download size={14} />
          <span>Download</span>
        </button>
      </div>

      {/* Active role groups */}
      {Object.entries(activeGroups).map(([role, members]) => {
        const isCollapsed = collapsedGroups[role];
        const groupHours = members.reduce((sum, { member }) => sum + (member.Assigned_Hours || 0), 0);
        const groupExpected = members.reduce((sum, { member }) => sum + (member.Changed_Expected_Hours || member.Weekly_Expected_Hours || 0), 0);

        return (
          <div key={role} className="team-role-group">
            <div className="team-role-group__header" onClick={() => setCollapsedGroups(prev => ({ ...prev, [role]: !prev[role] }))}>
              <div className="team-role-group__title">
                <span>{displayRole(role)}</span>
                <span className="team-role-group__count">{members.length}</span>
                {groupHours > 0 && <span className="team-role-group__hours">{groupHours.toFixed(0)}/{groupExpected}h</span>}
              </div>
              {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </div>
            {!isCollapsed && (
              <div className="team-role-group__members">
                {members.map(renderMemberCard)}
              </div>
            )}
          </div>
        );
      })}

      {/* Inactive members */}
      {inactiveMembers.length > 0 && (
        <div className="team-role-group team-role-group--inactive">
          <div className="team-role-group__header" onClick={() => setShowInactive(!showInactive)}>
            <div className="team-role-group__title">
              <span>Inactive</span>
              <span className="team-role-group__count">{inactiveMembers.length}</span>
            </div>
            {showInactive ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
          {showInactive && (
            <div className="team-role-group__members">
              {inactiveMembers.map(renderMemberCard)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamOverview;
